import {
  UpdateAdClickCountApiRequest,
  UpdateAdViewCountApiRequest,
} from "../api/BackgroundController";
import { useContent } from "../contexts/contentContext/contentContext";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  selectCampaignIdViewCounts,
  selectLogId,
} from "../store/selectors/backgroundSelectors";
import {
  incCampaignIdViewCount as incCampaignIdViewCountAction,
  setCampaignIdExpirationDate,
  setLogId,
} from "../store/slices/backgroundSlice";

/**
 * This hook incapsulates logic for background image on Home and Gallery (Download) pages.
 * Here is the logic for work with background slice from store
 * and handling API requests from BackgroundController.ts
 */

//TODO: Move all the logic regarding background image to this hook

const DESKTOP_MIN_WIDTH = 900;
const IS_DESKTOP = window.innerWidth > DESKTOP_MIN_WIDTH;

const useBackground = () => {
  const dispatch = useAppDispatch();

  const { getAdsBackground } = useContent();

  const logId = useAppSelector(selectLogId);
  const campaignIdViewCounts = useAppSelector(selectCampaignIdViewCounts);

  const updateLogId = (logId: number | undefined) => {
    dispatch(setLogId(logId));
  };

  /**
   * Update view count for the ad and set logId to the state
   * in order to use it for updating click count
   */
  const updateAdViewCount = async (payload?: {
    isDesktop?: boolean;
    adId?: number;
    location?: "Home" | "Download";
  }) => {
    try {
      const response = await UpdateAdViewCountApiRequest({
        ...payload,
        adId: payload?.adId || getAdsBackground().adId,
        location:
          payload?.location || window.location.pathname.includes("home")
            ? "Home"
            : "Download",
        isDesktop: payload?.isDesktop || IS_DESKTOP,
      });

      if (response.status === "success" && response.data) {
        const logId = response.data;

        if (logId) {
          updateLogId(logId);
        }
      } else {
        updateLogId(undefined);
      }
    } catch (error) {
      console.error("Error while increasing view count", error);
    }
  };

  /**
   * Update click count for the ad using logId from the store
   */
  const updateAdClickCount = async (payload?: {
    isDesktop?: boolean;
    adId?: number;
  }) => {
    try {
      await UpdateAdClickCountApiRequest({
        ...payload,
        isDesktop: payload?.isDesktop || IS_DESKTOP,
        adId: payload?.adId || getAdsBackground().adId,
        logId,
      });
    } catch (error) {
      console.error("Error while increasing click count", error);
    }
  };

  const getCampaignIdViewCount = (campaignId: number) => {
    return campaignIdViewCounts?.[campaignId] || 0;
  };

  const incCampaignIdViewCount = (campaignId: number) => {
    dispatch(incCampaignIdViewCountAction(campaignId));
  };

  const updateCampaignIdExpirationDate = (
    campaignId: number,
    expirationDate?: number
  ) => {
    dispatch(setCampaignIdExpirationDate({ campaignId, expirationDate }));
  };

  const getCampaignIdsForExcluding = () => {
    if (campaignIdViewCounts) {
      const res = Object.entries(campaignIdViewCounts).map(
        ([campaignId, { expirationDate }]) => {
          // Check if expiration date is not expired which means user cannot see this ad
          if (expirationDate && expirationDate > Date.now()) {
            return +campaignId;
          }
        }
      );

      return res.filter((campaignId) => !!campaignId);
    } else return [];
  };

  return {
    logId,
    updateLogId,
    updateAdViewCount,
    updateAdClickCount,
    getCampaignIdViewCount,
    campaignIdViewCounts,
    incCampaignIdViewCount,
    updateCampaignIdExpirationDate,
    getCampaignIdsForExcluding,
  };
};

export default useBackground;
